/*
 * ajax-cart-drawer.js — Bumi Glow slide-out cart (vanilla JS, no jQuery).
 * Companion to /snippets/ajax-cart-drawer.liquid.
 *
 * Responsibilities:
 *  - Intercept add-to-cart form submits + [data-add-to-cart] clicks -> Cart AJAX API.
 *  - Open the drawer and re-render line items, subtotal, count, and free-shipping bar
 *    asynchronously from /cart.js — no page reload.
 *  - Quantity change / remove via /cart/change.js.
 *  - Populate an upsell rail from the product recommendations endpoint.
 */
(function () {
  'use strict';

  var cfg = window.CartDrawerConfig || {};
  var routes = cfg.routes || {};
  var drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  var els = {
    overlay: drawer.querySelector('[data-cd-overlay]'),
    panel: drawer.querySelector('[data-cd-panel]'),
    close: drawer.querySelector('[data-cd-close]'),
    items: drawer.querySelector('[data-cd-items]'),
    empty: drawer.querySelector('[data-cd-empty]'),
    count: drawer.querySelectorAll('[data-cd-count]'),
    subtotal: drawer.querySelector('[data-cd-subtotal]'),
    shipbarText: drawer.querySelector('[data-cd-shipbar-text]'),
    shipbarFill: drawer.querySelector('[data-cd-shipbar-fill]'),
    footer: drawer.querySelector('[data-cd-footer]'),
    upsell: drawer.querySelector('[data-cd-upsell]'),
    upsellList: drawer.querySelector('[data-cd-upsell-list]'),
    itemTemplate: drawer.querySelector('[data-cd-item-template]')
  };
  var threshold = parseInt(drawer.getAttribute('data-free-shipping-threshold'), 10) || 0;
  var lastFocus = null;

  /* ---------- money formatting (Shopify money_format) ---------- */
  function formatMoney(cents) {
    var value = (cents || 0);
    var fmt = cfg.moneyFormat || '${{amount}}';
    function num(n, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var s = (Math.abs(n) / 100).toFixed(precision);
      var parts = s.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount': return num(value, 2);
        case 'amount_no_decimals': return num(value, 0);
        case 'amount_with_comma_separator': return num(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return num(value, 0, '.', ',');
        default: return num(value, 2);
      }
    });
  }

  /* ---------- open / close + focus trap ---------- */
  function openDrawer() {
    lastFocus = document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    /* force reflow so the transition runs */
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    (els.close || els.panel).focus();
    document.addEventListener('keydown', onKeydown);
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    var onEnd = function () {
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', onEnd);
    };
    drawer.addEventListener('transitionend', onEnd);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function onKeydown(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var focusable = els.panel.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- render ---------- */
  function setBusy(on) { drawer.classList.toggle('cd--busy', !!on); }

  function updateCounts(count) {
    els.count.forEach(function (el) { el.textContent = count; });
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = count; });
    /* theme header badge (.cartcount): update text + show/hide at zero */
    document.querySelectorAll('.cartcount').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: count } }));
  }

  function renderShipbar(total) {
    if (!threshold || !els.shipbarText) return;
    if (total >= threshold) {
      els.shipbarText.textContent = cfg.strings.freeShippingQualified;
      els.shipbarFill.style.width = '100%';
    } else {
      var remaining = threshold - total;
      els.shipbarText.textContent =
        (cfg.strings.freeShippingRemaining || 'Spend {amount} more for free shipping')
          .replace('{amount}', formatMoney(remaining));
      els.shipbarFill.style.width = Math.min(100, (total / threshold) * 100) + '%';
    }
  }

  function renderItem(line) {
    var node = els.itemTemplate.content.firstElementChild.cloneNode(true);
    var img = node.querySelector('[data-cd-item-img]');
    if (line.image) { img.src = line.image.replace(/(\.[^.]*)$/, '_160x160$1'); img.alt = line.product_title; }
    node.querySelector('[data-cd-item-link]').href = line.url;
    var title = node.querySelector('[data-cd-item-title]');
    title.textContent = line.product_title; title.href = line.url;
    var variant = node.querySelector('[data-cd-item-variant]');
    variant.textContent = line.variant_title || '';
    if (!line.variant_title) variant.hidden = true;
    node.querySelector('[data-cd-item-price]').textContent = formatMoney(line.final_line_price);
    var qtyInput = node.querySelector('[data-cd-qty-input]');
    qtyInput.value = line.quantity;
    node.setAttribute('data-key', line.key);
    return node;
  }

  function renderCart(cart) {
    els.items.innerHTML = '';
    var empty = cart.item_count === 0;
    els.empty.hidden = !empty;
    els.footer.style.display = empty ? 'none' : '';
    if (els.upsell) els.upsell.hidden = empty;

    cart.items.forEach(function (line) { els.items.appendChild(renderItem(line)); });
    els.subtotal.textContent = formatMoney(cart.total_price);
    updateCounts(cart.item_count);
    renderShipbar(cart.total_price);
    if (!empty) loadUpsells(cart.items[0].product_id);
  }

  /* ---------- Cart AJAX API ---------- */
  function getCart() {
    return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }
  function changeLine(key, quantity) {
    setBusy(true);
    return fetch(routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); })
      .finally(function () { setBusy(false); });
  }
  function addToCart(formDataOrBody) {
    setBusy(true);
    return fetch(routes.cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formDataOrBody
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () { return getCart(); })
      .then(function (cart) { renderCart(cart); openDrawer(); })
      .catch(function (err) {
        console.warn('[cart-drawer] add failed', err);
        alert((err && err.description) || 'Sorry, this item could not be added.');
      })
      .finally(function () { setBusy(false); });
  }

  /* ---------- upsells ---------- */
  function loadUpsells(productId) {
    if (!els.upsellList || !routes.recommendations) return;
    fetch(routes.recommendations + '.json?product_id=' + productId + '&limit=4&intent=related',
      { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.products) || [];
        els.upsellList.innerHTML = '';
        if (!products.length) { els.upsell.hidden = true; return; }
        products.forEach(function (p) {
          var variant = (p.variants || []).find(function (v) { return v.available; }) || p.variants[0];
          if (!variant) return;
          var li = document.createElement('li');
          li.className = 'cd__upsell-item';
          li.innerHTML =
            '<a href="' + p.url + '"><img src="' + (p.featured_image || '') +
            '" alt="" width="140" height="140" loading="lazy"></a>' +
            '<p class="cd__upsell-name">' + p.title + '</p>' +
            '<span class="cd__upsell-price">' + formatMoney(variant.price) + '</span>' +
            '<button type="button" class="cd__upsell-add" data-cd-upsell-add="' + variant.id + '">Add</button>';
          els.upsellList.appendChild(li);
        });
      })
      .catch(function () { if (els.upsell) els.upsell.hidden = true; });
  }

  /* ---------- event wiring ---------- */
  els.overlay.addEventListener('click', closeDrawer);
  els.close.addEventListener('click', closeDrawer);

  /* Intercept every add-to-cart form on the page */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    addToCart(new FormData(form));
  });

  /* Delegate qty / remove / upsell clicks inside the drawer */
  drawer.addEventListener('click', function (e) {
    var itemEl = e.target.closest('[data-cd-item]');
    if (itemEl) {
      var key = itemEl.getAttribute('data-key');
      var input = itemEl.querySelector('[data-cd-qty-input]');
      if (e.target.closest('[data-cd-inc]')) { changeLine(key, parseInt(input.value, 10) + 1); return; }
      if (e.target.closest('[data-cd-dec]')) { changeLine(key, Math.max(0, parseInt(input.value, 10) - 1)); return; }
      if (e.target.closest('[data-cd-remove]')) { changeLine(key, 0); return; }
    }
    var addBtn = e.target.closest('[data-cd-upsell-add]');
    if (addBtn) {
      var body = new FormData();
      body.append('id', addBtn.getAttribute('data-cd-upsell-add'));
      body.append('quantity', '1');
      addToCart(body);
    }
  });

  drawer.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cd-qty-input]');
    if (!input) return;
    var itemEl = input.closest('[data-cd-item]');
    changeLine(itemEl.getAttribute('data-key'), Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* Allow other UI (header cart icon) to open the drawer */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cart-drawer-open], a[href$="/cart"]');
    if (!trigger) return;
    if (trigger.hasAttribute('data-cart-drawer-open') || trigger.getAttribute('href').replace(/\?.*$/, '').endsWith('/cart')) {
      e.preventDefault();
      getCart().then(renderCart).then(openDrawer);
    }
  });

  /* Prime the drawer contents on load (so the first open is instant) */
  getCart().then(renderCart).catch(function () {});
})();
